import { useStripe } from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import React, { useEffect, useMemo } from 'react';
import { useUrlSearchParams } from 'use-url-search-params';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { PaymentStatus } from '../../types';
import { redirectFpxQuery } from '../../utils/constants';

interface useProcessingPaymentFpx {
  onComplete: ({
    stripeError,
    status,
    error,
  }: {
    stripeError?: StripeError;
    status?: PaymentStatus;
    error?: any;
  }) => void;
}

const useProcessingPaymentFpx = ({ onComplete }: useProcessingPaymentFpx) => {
  const { sendLinkPaymentProcessingEvent } = useAnalytics();
  const stripe = useStripe();

  const [params, setParams] = useUrlSearchParams();

  // Remove Stripe SDK and payments-widget added params
  const removeParams = React.useCallback(() => {
    const finalParam: any = {};
    Object.keys(params).forEach((k) => {
      // only remove payments-widget added params for FPX redirect purpose
      if (
        k.includes('payment_intent') ||
        k.includes('payment_intent_client_secret') ||
        k.includes('redirect_status') ||
        k.includes('payments_return') ||
        k.includes('cartid') ||
        k.includes('orderid')
      ) {
        finalParam[k] = undefined;
      } else {
        finalParam[k] = params[k];
      }
    });
    setParams(finalParam);
  }, [params, setParams]);

  const isFPXReturn = useMemo(
    () => params[redirectFpxQuery.key] === redirectFpxQuery.value,
    [params],
  );

  React.useEffect(() => {
    if (!stripe) {
      return;
    }
  }, [stripe]);

  const checkStatus = React.useCallback(async () => {
    const startTime = performance.now();
    if (!stripe) {
      return;
    }
    const clientSecret = localStorage.getItem('processingPaymentDataFPX');
    if (!clientSecret) {
      return;
    }
    try {
      const { error, paymentIntent } =
        await stripe.retrievePaymentIntent(clientSecret);
      removeParams();
      localStorage.removeItem('processingPaymentDataFPX');
      const endTime = performance.now();
      sendLinkPaymentProcessingEvent('Fpx', endTime - startTime);
      onComplete({ stripeError: error, status: paymentIntent?.status });
    } catch (error) {
      removeParams();
      onComplete({ error });
    }
  }, [onComplete, removeParams, sendLinkPaymentProcessingEvent, stripe]);

  useEffect(() => {
    if (isFPXReturn && stripe) {
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  return [isFPXReturn];
};

export default useProcessingPaymentFpx;
